import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  Globe,
  Hash,
  Key,
  Pause,
  Play,
  Plus,
  Search,
  Trash2,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CopyId, StatusPill } from '@/components/page';
import { AccessSettingsFields, type AccessSettingsValue } from '@/components/products/AccessFields';
import { ProductAccessPanel, ProductRolesPanel } from '@/components/products/ProductAccessWorkspace';
import {
  ACCESS_MODELS,
  AccessTag,
  AppMark,
  CardHead,
  ConfirmDialog,
  EmptyState,
  FieldError,
  KV,
  SLUG_RE,
  Tag,
  ago,
  fmtDate,
  fmtDateTime,
  grantStateOf,
  hostOf,
  slugify,
  validUrl,
} from '@/components/products/shared';
import { cn } from '@/lib/utils';
import {
  useAuth,
  useClients,
  useDeleteProduct,
  useOrganizationMembers,
  useOrganizations,
  useProduct,
  useProductAccess,
  useProductRoles,
  useProducts,
  useToast,
  useUpdateProduct,
} from '@/hooks';
import type { OAuthClient, Product, ProductAccessModel } from '@/types';

const SYSTEM_ORG_ID = 'org-system';
const TABS = ['overview', 'roles', 'access', 'clients', 'settings'] as const;
type Tab = (typeof TABS)[number];

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canManage = user?.is_super_admin === true;
  const { data: product, isLoading, isError } = useProduct(id);
  const { data: roles = [] } = useProductRoles(id);
  const { data: grants = [] } = useProductAccess(id);
  const { data: clients = [] } = useClients(id);

  const fromHash = window.location.hash.replace('#', '');
  const requested = params.get('tab') ?? fromHash;
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : 'overview';
  const setTab = (t: string) => {
    const next = new URLSearchParams(params);
    if (t === 'overview') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  const [grantSignal, setGrantSignal] = useState(0);
  const [roleSignal, setRoleSignal] = useState(0);

  useEffect(() => {
    if (product) document.title = `${product.name} · authrz.me`;
  }, [product]);

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading app">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 animate-pulse rounded-[10px] bg-secondary" />
          <div className="space-y-2">
            <div className="h-6 w-48 animate-pulse rounded bg-secondary" />
            <div className="h-4 w-32 animate-pulse rounded bg-secondary" />
          </div>
        </div>
        <div className="h-64 animate-pulse rounded-lg bg-secondary/60" />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <Card>
        <EmptyState
          icon={Search}
          title="App not found"
          body="This app doesn’t exist or you don’t have access to it."
          action={
            <Button variant="outline" asChild>
              <Link to="/products">Back to apps</Link>
            </Button>
          }
        />
      </Card>
    );
  }

  const settings = product.settings ?? {};
  const model: ProductAccessModel = settings.access_model ?? 'gated';
  const orgLinked = product.organization_id && product.organization_id !== SYSTEM_ORG_ID;

  return (
    <div>
      {!product.is_active && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/[0.08] p-3.5">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          <div className="min-w-0 flex-1">
            <b className="text-[13.5px] font-semibold">This app is deactivated</b>
            <p className="text-[13px] text-muted-foreground">
              Sign-ins and token requests for its clients are refused until it’s reactivated.
            </p>
          </div>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => setTab('settings')}>
              Review settings
            </Button>
          )}
        </div>
      )}

      <header className="mb-6 flex flex-wrap items-start gap-3.5">
        <AppMark name={product.name} size="lg" />
        <div className="min-w-[240px] flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="truncate text-2xl font-semibold leading-tight tracking-[-0.03em]">
              {product.name}
            </h1>
            <StatusPill tone={product.is_active ? 'ok' : 'neutral'}>
              {product.is_active ? 'Active' : 'Inactive'}
            </StatusPill>
            <AccessTag model={model} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" />
              <code className="text-xs">{product.slug}</code>
            </span>
            {orgLinked && (
              <Link
                to={`/organizations/${product.organization_id}`}
                className="inline-flex items-center gap-1.5 text-primary hover:underline"
              >
                <Building2 className="h-3.5 w-3.5" />
                {product.organization_name || product.organization_id}
              </Link>
            )}
            {settings.resource && (
              <span className="inline-flex items-center gap-1.5" title="Resource audience">
                <Globe className="h-3.5 w-3.5" />
                <code className="text-xs">{hostOf(settings.resource)}</code>
              </span>
            )}
            <span
              className="inline-flex items-center gap-1.5"
              title={fmtDateTime(product.updated_at)}
            >
              <Clock className="h-3.5 w-3.5" />
              Updated {ago(product.updated_at).replace('Just now', 'just now')}
            </span>
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to={`/clients/new?product=${product.id}`}>
                <Key className="h-4 w-4" />
                New client
              </Link>
            </Button>
            <Button
              onClick={() => {
                setTab('access');
                setGrantSignal((n) => n + 1);
              }}
            >
              <UserPlus className="h-4 w-4" />
              Grant access
            </Button>
          </div>
        )}
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 flex w-full overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="roles">
            Roles
            <Count n={roles.length} />
          </TabsTrigger>
          <TabsTrigger value="access">
            Access
            <Count n={grants.length} />
          </TabsTrigger>
          <TabsTrigger value="clients">
            Clients
            <Count n={clients.length} />
          </TabsTrigger>
          {canManage && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <Overview product={product} clients={clients} canManage={canManage} />
        </TabsContent>
        <TabsContent value="roles" className="mt-0">
          <ProductRolesPanel productId={product.id} product={product} signal={roleSignal} />
        </TabsContent>
        <TabsContent value="access" className="mt-0">
          <ProductAccessPanel
            productId={product.id}
            product={product}
            signal={grantSignal}
            onDefineRole={() => {
              setTab('roles');
              setRoleSignal((n) => n + 1);
            }}
          />
        </TabsContent>
        <TabsContent value="clients" className="mt-0">
          <ClientsPanel product={product} clients={clients} canManage={canManage} />
        </TabsContent>
        {canManage && (
          <TabsContent value="settings" className="mt-0">
            <SettingsPanel
              product={product}
              roleCount={roles.length}
              grantCount={grants.length}
              clientCount={clients.length}
              roleNames={roles.map((r) => r.name)}
              onDeleted={() => navigate('/products')}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Count({ n }: { n: number }) {
  return (
    <span className="rounded-full bg-foreground/[0.06] px-1.5 font-mono text-[11px] text-muted-foreground">
      {n}
    </span>
  );
}

/* ── Overview ─────────────────────────────────────────── */

function Overview({
  product,
  clients,
  canManage,
}: {
  product: Product;
  clients: OAuthClient[];
  canManage: boolean;
}) {
  const settings = product.settings ?? {};
  const model: ProductAccessModel = settings.access_model ?? 'gated';
  const m = ACCESS_MODELS[model];
  const { data: grants = [] } = useProductAccess(product.id);
  const memberOrgId = model === 'org_members' ? settings.member_org_id || undefined : undefined;
  const { data: members = [] } = useOrganizationMembers(memberOrgId);
  const { data: orgs = [] } = useOrganizations();
  const memberOrgName = orgs.find((o) => o.id === memberOrgId)?.name;

  const by = (s: string) => grants.filter((g) => grantStateOf(g) === s).length;
  const live = by('active') + by('expiring');
  const users = (n: number) => `${n} ${n === 1 ? 'user' : 'users'}`;

  const rules: { on: boolean; title: React.ReactNode; sub: React.ReactNode }[] = {
    gated: [
      { on: true, title: `${users(live)} with an active grant`, sub: 'Roles and permissions come from each grant.' },
      { on: false, title: `${by('suspended') + by('expired')} suspended or expired`, sub: 'Kept for restore, but refused at sign-in.' },
      { on: false, title: 'Everyone else', sub: 'Refused with no_access.' },
    ],
    org_members: [
      {
        on: true,
        title: `${members.length} ${members.length === 1 ? 'member' : 'members'} of ${memberOrgName || 'the organization'}`,
        sub: settings.default_role ? (
          <>
            Signed in with the <code>{settings.default_role}</code> role.
          </>
        ) : (
          'Signed in with no default role.'
        ),
      },
      { on: true, title: `${users(live)} with an active grant`, sub: 'Their grant’s roles are used instead.' },
      { on: false, title: 'Everyone else', sub: 'Refused with no_access.' },
    ],
    open: [
      { on: true, title: 'Any signed-in authrz.me user', sub: 'No grant needed to sign in.' },
      { on: true, title: `${users(live)} with roles`, sub: 'Active grants add roles and permissions to their tokens.' },
    ],
  }[model];

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <Card>
        <CardHead title="Authorization" sub="How tokens for this app are issued." />
        <div className="p-[18px]">
          <KV
            rows={[
              { label: 'App ID', value: <CopyId value={product.id} /> },
              { label: 'Slug', value: <CopyId value={product.slug} /> },
              {
                label: 'Organization',
                value:
                  product.organization_id && product.organization_id !== SYSTEM_ORG_ID ? (
                    <Link
                      to={`/organizations/${product.organization_id}`}
                      className="text-primary hover:underline"
                    >
                      {product.organization_name || product.organization_id}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  ),
              },
              {
                label: 'Resource audience',
                value: (
                  <div className="space-y-1.5">
                    {settings.resource ? (
                      <CopyId value={settings.resource} />
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Optional RFC 8707 value. When set, tokens for this app use it as{' '}
                      <code>aud</code>.
                    </p>
                  </div>
                ),
              },
              {
                label: 'Access model',
                value: (
                  <div className="space-y-1.5">
                    <AccessTag model={model} />
                    <p className="text-xs text-muted-foreground">{m.desc}</p>
                  </div>
                ),
              },
              {
                label: 'Default role',
                value:
                  model === 'org_members' ? (
                    settings.default_role ? (
                      <Tag>{settings.default_role}</Tag>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )
                  ) : (
                    <span className="text-muted-foreground">
                      Only used with organization-member access
                    </span>
                  ),
              },
              {
                label: 'Permissions in tokens',
                value: (
                  <div className="space-y-1.5">
                    <span className="inline-flex items-center gap-2">
                      <Switch
                        checked={settings.include_permissions_in_token !== false}
                        disabled
                        aria-label="Include permissions in access tokens"
                      />
                      {settings.include_permissions_in_token !== false ? 'Included' : 'Not included'}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      Roles are always emitted when the <code>products</code> scope is requested.
                    </p>
                  </div>
                ),
              },
              {
                label: 'Created',
                value: <span className="font-mono text-xs tabular-nums">{fmtDate(product.created_at)}</span>,
              },
              {
                label: 'Description',
                value: product.description ? (
                  <p>{product.description}</p>
                ) : (
                  <p className="text-muted-foreground">No description.</p>
                ),
              },
            ]}
          />
        </div>
      </Card>

      <div className="space-y-5">
        <Card>
          <CardHead title="Who can sign in" sub={m.title} />
          <div className="grid gap-2.5 p-[18px]">
            {rules.map((r, i) => (
              <div key={i} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2.5 text-[13.5px]">
                {r.on ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 text-muted-foreground" />
                )}
                <div>
                  <b className="block font-semibold">{r.title}</b>
                  <span className="block text-[12.5px] text-muted-foreground">{r.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <CardHead title="OAuth clients" sub="Applications that request tokens for this app." />
          {clients.length ? (
            <div className="divide-y divide-border">
              {clients.map((c) => (
                <Link
                  key={c.id}
                  to={`/clients/${c.id}`}
                  className="flex items-center gap-3 px-[18px] py-3 hover:bg-foreground/[0.03]"
                >
                  <span className="min-w-0 flex-1">
                    <b className="block truncate font-semibold">{c.name}</b>
                    <span
                      className="block truncate font-mono text-xs text-muted-foreground"
                      title={c.client_id}
                    >
                      {c.client_id}
                    </span>
                  </span>
                  <Tag>{c.client_type}</Tag>
                  {!c.is_active && <StatusPill>Inactive</StatusPill>}
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Key}
              title="No clients yet"
              body="Register a client to start an OAuth flow for this app."
              action={
                canManage ? (
                  <Button variant="outline" asChild>
                    <Link to={`/clients/new?product=${product.id}`}>
                      <Plus className="h-4 w-4" />
                      New client
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}

/* ── Clients ──────────────────────────────────────────── */

function ClientsPanel({
  product,
  clients,
  canManage,
}: {
  product: Product;
  clients: OAuthClient[];
  canManage: boolean;
}) {
  const navigate = useNavigate();
  const newClient = (
    <Button size="sm" variant="outline" asChild>
      <Link to={`/clients/new?product=${product.id}`}>
        <Plus className="h-4 w-4" />
        New client
      </Link>
    </Button>
  );
  return (
    <Card className="overflow-hidden">
      <CardHead
        title="OAuth clients"
        sub="Each client has its own redirect URIs, scopes and credentials."
        action={canManage && clients.length > 0 ? newClient : undefined}
      />
      {clients.length === 0 ? (
        <EmptyState
          icon={Key}
          title="No OAuth clients"
          body="Create a client for each web app, mobile app, CLI or service that requests tokens."
          action={canManage ? newClient : undefined}
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="hidden md:table-cell">Grant types</TableHead>
              <TableHead className="hidden text-right md:table-cell">Redirect URIs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-8">
                <span className="sr-only">Open</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((c) => (
              <TableRow
                key={c.id}
                className={cn('cursor-pointer', !c.is_active && 'opacity-60')}
                onClick={() => navigate(`/clients/${c.id}`)}
              >
                <TableCell>
                  <Link
                    to={`/clients/${c.id}`}
                    className="block min-w-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <b className="block max-w-[260px] truncate font-semibold">{c.name}</b>
                    <span
                      className="block max-w-[260px] truncate font-mono text-xs text-muted-foreground"
                      title={c.client_id}
                    >
                      {c.client_id}
                    </span>
                  </Link>
                </TableCell>
                <TableCell>
                  <Tag>{c.client_type}</Tag>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <div className="flex flex-wrap gap-1.5">
                    {c.allowed_grant_types.map((t) => (
                      <Tag key={t} solid>
                        {t}
                      </Tag>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="hidden text-right font-mono tabular-nums md:table-cell">
                  {c.redirect_uris.length}
                </TableCell>
                <TableCell>
                  <StatusPill tone={c.is_active ? 'ok' : 'neutral'}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </StatusPill>
                </TableCell>
                <TableCell className="w-8 text-muted-foreground">
                  <ChevronRight className="h-4 w-4" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

/* ── Settings ─────────────────────────────────────────── */

function SettingsPanel({
  product,
  roleCount,
  grantCount,
  clientCount,
  roleNames,
  onDeleted,
}: {
  product: Product;
  roleCount: number;
  grantCount: number;
  clientCount: number;
  roleNames: string[];
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const { data: others = [] } = useProducts();
  const { data: orgs = [] } = useOrganizations();

  const initial = useMemo(
    () => ({
      name: product.name,
      slug: product.slug,
      description: product.description ?? '',
      access: {
        access_model: product.settings?.access_model ?? 'gated',
        default_role: product.settings?.default_role ?? '',
        member_org_id: product.settings?.member_org_id ?? '',
        resource: product.settings?.resource ?? '',
        include_permissions_in_token: product.settings?.include_permissions_in_token ?? true,
      } as AccessSettingsValue,
    }),
    [product]
  );

  const [name, setName] = useState(initial.name);
  const [slug, setSlug] = useState(initial.slug);
  const [description, setDescription] = useState(initial.description);
  const [access, setAccess] = useState<AccessSettingsValue>(initial.access);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const reset = () => {
    setName(initial.name);
    setSlug(initial.slug);
    setDescription(initial.description);
    setAccess(initial.access);
    setErrors({});
  };
  useEffect(reset, [initial]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Enter a name.';
    const s = slug.trim();
    if (!s) e.slug = 'Enter a slug.';
    else if (!SLUG_RE.test(s)) e.slug = 'Use lowercase letters, numbers and single hyphens.';
    else if (others.some((p) => p.slug === s && p.id !== product.id))
      e.slug = 'Another app already uses this slug.';
    if (!validUrl(access.resource.trim())) e.resource = 'Enter an absolute URL, like https://api.example.com';
    if (access.access_model === 'org_members' && !access.member_org_id)
      e.member_org_id = 'Choose the organization whose members can sign in.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    const changed: string[] = [];
    if (name.trim() !== initial.name) changed.push('name');
    if (slug.trim() !== initial.slug) changed.push('slug');
    if (description.trim() !== initial.description) changed.push('description');
    (Object.keys(access) as (keyof AccessSettingsValue)[]).forEach((k) => {
      if (access[k] !== initial.access[k]) changed.push(k.replace(/_/g, ' '));
    });
    if (!changed.length) {
      toast({ title: 'No changes to save' });
      return;
    }
    try {
      await updateProduct.mutateAsync({
        id: product.id,
        data: {
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim(),
          settings: {
            ...product.settings,
            access_model: access.access_model,
            resource: access.resource.trim() || undefined,
            default_role: access.access_model === 'org_members' ? access.default_role : undefined,
            member_org_id: access.access_model === 'org_members' ? access.member_org_id : undefined,
            include_permissions_in_token: access.include_permissions_in_token,
          },
        },
      });
      toast({ title: 'App saved', description: `Updated ${changed.join(', ')}.` });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Failed to update the app',
      });
    }
  };

  const toggleActive = async () => {
    try {
      await updateProduct.mutateAsync({ id: product.id, data: { is_active: !product.is_active } });
      toast({
        title: product.is_active ? 'App deactivated' : 'App reactivated',
        description: product.name,
      });
      setConfirmToggle(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Failed to update the app',
      });
    }
  };

  const remove = async () => {
    try {
      await deleteProduct.mutateAsync(product.id);
      toast({ title: 'App deleted', description: `${product.name} and its clients were removed.` });
      setConfirmDelete(false);
      onDeleted();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete the app',
      });
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={save} noValidate className="space-y-5">
        <Card>
          <CardHead
            title="App details"
            sub={
              <>
                Changing the slug changes the key used in the <code>products</code> token claim.
              </>
            }
          />
          <div className="grid gap-4 p-[18px] sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="s-name">Name</Label>
              <Input
                id="s-name"
                required
                maxLength={80}
                value={name}
                aria-invalid={!!errors.name}
                onChange={(e) => setName(e.target.value)}
              />
              <FieldError>{errors.name}</FieldError>
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-slug">Slug</Label>
              <Input
                id="s-slug"
                required
                maxLength={48}
                className="font-mono"
                spellCheck={false}
                autoCapitalize="off"
                value={slug}
                aria-invalid={!!errors.slug}
                onChange={(e) => setSlug(e.target.value)}
                onBlur={() => setSlug((s) => slugify(s) || s.trim())}
              />
              <FieldError>{errors.slug}</FieldError>
              <p className="text-xs text-muted-foreground">Lowercase letters, numbers and hyphens.</p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="s-desc">
                Description
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
              </Label>
              <Textarea
                id="s-desc"
                maxLength={280}
                placeholder="What people use this app for"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </Card>

        <Card>
          <CardHead
            title="Authorization"
            sub="Controls who can sign in and what goes into their tokens."
          />
          <div className="p-[18px]">
            <AccessSettingsFields
              idPrefix="s"
              value={access}
              onChange={setAccess}
              roleNames={roleNames}
              orgs={orgs}
              errors={errors}
            />
          </div>
          <div className="flex flex-col-reverse justify-end gap-3 border-t border-border px-[18px] py-3.5 sm:flex-row">
            <Button type="button" variant="outline" onClick={reset}>
              Discard changes
            </Button>
            <Button type="submit" disabled={updateProduct.isPending}>
              {updateProduct.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Card>
      </form>

      <Card className="border-destructive/35">
        <CardHead
          title="Danger zone"
          sub={`These actions affect every client and grant of ${product.name}.`}
        />
        <div className="divide-y divide-border">
          <div className="flex flex-wrap items-center justify-between gap-4 px-[18px] py-4">
            <div className="min-w-0 max-w-[60ch]">
              <b className="font-semibold">{product.is_active ? 'Deactivate app' : 'Reactivate app'}</b>
              <p className="text-[13px] text-muted-foreground">
                {product.is_active
                  ? 'Refuses sign-ins and token requests for all of its clients. Roles, grants and clients are kept.'
                  : 'Lets its clients request tokens again.'}
              </p>
            </div>
            <Button
              variant="outline"
              className={product.is_active ? 'text-destructive hover:text-destructive' : undefined}
              onClick={() => setConfirmToggle(true)}
            >
              {product.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {product.is_active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 px-[18px] py-4">
            <div className="min-w-0 max-w-[60ch]">
              <b className="font-semibold">Delete app</b>
              <p className="text-[13px] text-muted-foreground">
                Permanently deletes the app with its {roleCount} roles, {grantCount} grants and{' '}
                {clientCount} OAuth clients. Tokens already issued stay valid until they expire.
              </p>
            </div>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmToggle}
        onOpenChange={setConfirmToggle}
        title={product.is_active ? `Deactivate ${product.name}?` : `Reactivate ${product.name}?`}
        body={
          product.is_active
            ? `All ${clientCount} clients stop receiving tokens. Existing tokens stay valid until they expire.`
            : 'Its clients can request tokens again.'
        }
        confirmLabel={product.is_active ? 'Deactivate app' : 'Reactivate app'}
        pendingLabel="Saving…"
        danger={product.is_active}
        pending={updateProduct.isPending}
        onConfirm={toggleActive}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${product.name}?`}
        body={`This permanently deletes the app, ${roleCount} roles, ${grantCount} grants and ${clientCount} OAuth clients. It can’t be undone.`}
        confirmLabel="Delete app"
        pendingLabel="Deleting…"
        typeToConfirm={product.slug}
        danger
        pending={deleteProduct.isPending}
        onConfirm={remove}
      />
    </div>
  );
}
